import React, { useState, useEffect } from 'react';
import { FaSync, FaCheckSquare, FaRegSquare } from 'react-icons/fa';
import { Dropdown } from 'react-bootstrap';
import apiService from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

const EDRManagement = () => {
  const [edrProviders, setEdrProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('configuration');

  useEffect(() => {
    loadEDRProviders();
  }, []);

  const loadEDRProviders = async () => {
    setLoading(true);
    try {
      const providers = await apiService.getEDRProviders();
      setEdrProviders(providers);
    } catch (error) {
      console.error('Failed to load EDR providers:', error);
      setError('Failed to load EDR providers');
    } finally {
      setLoading(false);
    }
  };

  const createEDRProvider = async (providerData) => {
    setLoading(true);
    try {
      await apiService.createEDRProvider(providerData);
      await loadEDRProviders();
      setShowAddModal(false);
      setSuccess('EDR provider created successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Failed to create EDR provider:', error);
      setError('Failed to create EDR provider');
    } finally {
      setLoading(false);
    }
  };

  const updateEDRProvider = async (id, providerData) => {
    setLoading(true);
    try {
      await apiService.updateEDRProvider(id, providerData);
      await loadEDRProviders();
      setShowEditModal(false);
      setEditingProvider(null);
      setSuccess('EDR provider updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Failed to update EDR provider:', error);
      setError('Failed to update EDR provider');
    } finally {
      setLoading(false);
    }
  };

  const deleteEDRProvider = async (id) => {
    if (!window.confirm('Are you sure you want to delete this EDR provider?')) {
      return;
    }
    
    setLoading(true);
    try {
      await apiService.deleteEDRProvider(id);
      await loadEDRProviders();
      setSuccess('EDR provider deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Failed to delete EDR provider:', error);
      setError('Failed to delete EDR provider');
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async (provider) => {
    try {
      const result = await apiService.testEDRConnection(provider.provider, provider.config);
      if (result?.connected) {
        setSuccess(`Connection test successful for ${provider.name}`);
      } else {
        setError(`Connection test failed for ${provider.name}: ${result?.message || 'Unknown error'}`);
      }
      setTimeout(() => setSuccess(''), 3000);
      setTimeout(() => setError(''), 3000);
    } catch (error) {
      console.error('Failed to test connection:', error);
      setError(`Connection test failed for ${provider.name}: ${error.response?.data?.message || error.message}`);
      setTimeout(() => setError(''), 3000);
    }
  };

  const getProviderBadge = (provider) => {
    const badgeStyles = {
      sentinelone: 'bg-primary text-on-primary',
      crowdstrike: 'bg-success text-on-success',
      microsoft_defender: 'bg-info text-on-info',
      tanium: 'bg-warning text-on-warning',
    };
    
    const style = badgeStyles[provider.toLowerCase()] || 'bg-secondary text-on-secondary';
    const displayName = provider.replace('_', ' ').toUpperCase();
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${style}`}>
        {displayName}
      </span>
    );
  };

  if (loading && edrProviders.length === 0) {
    return (
      <div className="dash-ui-page">
        <LoadingSpinner message="Loading EDR providers..." />
      </div>
    );
  }

  return (
    <div className="dash-ui-page">
      <header className="dash-ui-appbar">
        <div className="dash-ui-appbar-title">
          <h2>EDR Provider Management</h2>
          <p>Configure and manage your Endpoint Detection & Response providers and their API connections</p>
        </div>
        <div className="dash-ui-appbar-right">
          <div className="dash-ui-appbar-actions">
            <button type="button" aria-label="Notifications">
              <span className="material-symbols-outlined">notifications</span>
            </button>
            <button type="button" aria-label="Help">
              <span className="material-symbols-outlined">help_outline</span>
            </button>
          </div>
          <div className="dash-ui-profile">
            <div>
              <p>Admin User</p>
              <span>System Administrator</span>
            </div>
          </div>
        </div>
      </header>

      <section className="dash-ui-control-strip">
        <button 
          type="button" 
          className="dash-ui-refresh-btn" 
          onClick={loadEDRProviders} 
          disabled={loading}
        >
          <FaSync />
          <span>{loading ? 'Refreshing...' : 'Refresh Now'}</span>
        </button>
        <button
          type="button"
          className="dash-ui-outline-btn"
          onClick={() => setShowAddModal(true)}
        >
          Add New Provider
        </button>
      </section>

      {error && (
        <div className="dash-ui-error">
          <span className="material-symbols-outlined">warning</span>
          <span>{error}</span>
          <button type="button" onClick={() => setError('')}>Dismiss</button>
        </div>
      )}

      {success && (
        <div className="dash-ui-error" style={{background: '#dcfce7', color: '#166534', borderColor: 'rgba(22, 101, 52, 0.25)'}}>
          <span className="material-symbols-outlined">check_circle</span>
          <span>{success}</span>
          <button type="button" onClick={() => setSuccess('')}>Dismiss</button>
        </div>
      )}

      {/* Provider Grid */}
      {edrProviders.length > 0 && (
        <div className="dash-ui-bento-grid">
          {edrProviders.map((provider) => (
            <article key={provider.id} className="dash-ui-card">
              <div className="dash-ui-card-head">
                <div>
                  <h4>{provider.name}</h4>
                  <p>{provider.description}</p>
                </div>
                <div className="flex items-center space-x-2">
                  {getProviderBadge(provider.provider)}
                  <div className={`dash-ui-badge ${
                    provider.enabled 
                      ? 'status-good' 
                      : 'status-bad'
                  }`}>
                    {provider.enabled ? 'Active' : 'Inactive'}
                  </div>
                </div>
              </div>
              
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  <div className="flex items-center space-x-3">
                    <span className="material-symbols-outlined text-primary">key</span>
                    <div className="flex-1">
                      <p className="text-xs font-semibold uppercase">API Token</p>
                      <p className="text-sm font-mono">{provider.config?.api_key ? '•••••••••••' : 'Not configured'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <span className="material-symbols-outlined text-success">link</span>
                    <div className="flex-1">
                      <p className="text-xs font-semibold uppercase">Base URL</p>
                      <p className="text-sm font-mono">{provider.config?.base_url || 'Not configured'}</p>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-2 pt-4 border-t">
                  <button
                    onClick={() => testConnection(provider)}
                    disabled={loading}
                    className="dash-ui-outline-btn"
                  >
                    Test Connection
                  </button>
                  <button
                    onClick={() => {
                      setEditingProvider(provider);
                      setShowEditModal(true);
                    }}
                    className="dash-ui-outline-btn"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => deleteEDRProvider(provider.id)}
                    disabled={loading}
                    className="dash-ui-outline-btn"
                    style={{borderColor: '#dc3545', color: '#dc3545'}}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Empty State */}
      {edrProviders.length === 0 && (
        <div className="dash-ui-card" style={{textAlign: 'center', padding: '3rem'}}>
          <span className="material-symbols-outlined text-6xl text-muted" style={{fontSize: '4rem', marginBottom: '1rem'}}>security</span>
          <h3>No EDR Providers Configured</h3>
          <p>Get started by adding your first EDR provider to begin monitoring security alerts and threats.</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="dash-ui-refresh-btn"
            style={{marginTop: '1rem'}}
          >
            Add Your First Provider
          </button>
        </div>
      )}

      {/* Modals */}
      <EDRProviderModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSubmit={createEDRProvider}
        loading={loading}
        title="Add EDR Provider"
      />

      <EDRProviderModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setEditingProvider(null);
        }}
        onSubmit={(data) => updateEDRProvider(editingProvider?.id, data)}
        loading={loading}
        title="Edit EDR Provider"
        initialData={editingProvider}
      />
    </div>
  );
};

// EDR Provider Form Modal Component
const createDefaultFormData = () => ({
  provider: 'sentinelone',
  name: '',
  description: '',
  config: {
    auth_type: 'management_api_token',
    api_key: '',
    log_access_key: '',
    configuration_access_key: '',
    base_url: '',
    xdr_base_url: '',
  },
  enabled: true,
});

const normalizeFormData = (initialData) => ({
  provider: initialData?.provider || 'sentinelone',
  name: initialData?.name || '',
  description: initialData?.description || '',
  config: {
    auth_type: initialData?.config?.auth_type || 'management_api_token',
    api_key: initialData?.config?.api_key || '',
    log_access_key: initialData?.config?.log_access_key || '',
    configuration_access_key: initialData?.config?.configuration_access_key || '',
    base_url: initialData?.config?.base_url || '',
    xdr_base_url: initialData?.config?.xdr_base_url || '',
  },
  enabled: initialData?.enabled ?? true,
});

const EDRProviderModal = ({ show, onHide, onSubmit, loading, title, initialData }) => {
  const [formData, setFormData] = useState(createDefaultFormData());
  const [showPassword, setShowPassword] = useState({});

  useEffect(() => {
    if (show) {
      setFormData(initialData ? normalizeFormData(initialData) : createDefaultFormData());
    }
  }, [initialData, show]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('config.')) {
      const configKey = name.substring(7);
      setFormData(prev => ({
        ...prev,
        config: {
          ...prev.config,
          [configKey]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const renderConfigFields = () => {
    const isSentinelOne = formData.provider === 'sentinelone';

    return (
      <>
        {isSentinelOne && (
          <>
            <div className="space-y-2">
              <label className="block text-sm font-semibold uppercase tracking-widest text-on-surface-variant">
                Management API Token
              </label>
              <div className="relative">
                <input
                  type={showPassword.api_key ? 'text' : 'password'}
                  name="config.api_key"
                  value={formData.config.api_key}
                  onChange={handleInputChange}
                  placeholder="Enter Management API Token"
                  required
                  className="w-full bg-surface-container-low border border-transparent focus:border-outline-variant focus:ring-0 rounded-md px-4 py-3 font-mono text-sm transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(prev => ({ ...prev, api_key: !prev.api_key }))}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-primary transition-colors"
                >
                  <span className="material-symbols-outlined text-sm">
                    {showPassword.api_key ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
              </div>
              <p className="text-xs text-on-surface-variant mt-1">
                Required. Used for SentinelOne management APIs, alerts, endpoints, activities, and standard report flows.
              </p>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-semibold uppercase tracking-widest text-on-surface-variant">
                Log Access Read Key
              </label>
              <div className="relative">
                <input
                  type={showPassword.log_access_key ? 'text' : 'password'}
                  name="config.log_access_key"
                  value={formData.config.log_access_key}
                  onChange={handleInputChange}
                  placeholder="Optional: Enter Log Access Read Key"
                  className="w-full bg-surface-container-low border border-transparent focus:border-outline-variant focus:ring-0 rounded-md px-4 py-3 font-mono text-sm transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(prev => ({ ...prev, log_access_key: !prev.log_access_key }))}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-primary transition-colors"
                >
                  <span className="material-symbols-outlined text-sm">
                    {showPassword.log_access_key ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
              </div>
              <p className="text-xs text-on-surface-variant mt-1">
                Optional. Used for richer SDL/XDR or log-access queries when available.
              </p>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-semibold uppercase tracking-widest text-on-surface-variant">
                Configuration Access Key
              </label>
              <div className="relative">
                <input
                  type={showPassword.configuration_access_key ? 'text' : 'password'}
                  name="config.configuration_access_key"
                  value={formData.config.configuration_access_key}
                  onChange={handleInputChange}
                  placeholder="Optional: Enter Configuration Access Key"
                  className="w-full bg-surface-container-low border border-transparent focus:border-outline-variant focus:ring-0 rounded-md px-4 py-3 font-mono text-sm transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(prev => ({ ...prev, configuration_access_key: !prev.configuration_access_key }))}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-primary transition-colors"
                >
                  <span className="material-symbols-outlined text-sm">
                    {showPassword.configuration_access_key ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
              </div>
              <p className="text-xs text-on-surface-variant mt-1">
                Optional. Keep this only if you use SentinelOne configuration APIs.
              </p>
            </div>
          </>
        )}
        {!isSentinelOne && (
          <div className="space-y-2">
            <label className="block text-sm font-semibold uppercase tracking-widest text-on-surface-variant">
              API Key
            </label>
            <div className="relative">
              <input
                type={showPassword.api_key ? 'text' : 'password'}
                name="config.api_key"
                value={formData.config.api_key}
                onChange={handleInputChange}
                placeholder="Enter API Key"
                required
                className="w-full bg-surface-container-low border border-transparent focus:border-outline-variant focus:ring-0 rounded-md px-4 py-3 font-mono text-sm transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword(prev => ({ ...prev, api_key: !prev.api_key }))}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-primary transition-colors"
              >
                <span className="material-symbols-outlined text-sm">
                  {showPassword.api_key ? 'visibility_off' : 'visibility'}
                </span>
              </button>
            </div>
          </div>
        )}
        
        <div className="space-y-2">
          <label className="block text-sm font-semibold uppercase tracking-widest text-on-surface-variant">
            Base URL
          </label>
          <div className="relative group">
            <input
              type="text"
              name="config.base_url"
              value={formData.config.base_url}
              onChange={handleInputChange}
              placeholder="https://api.your-provider.com/v1"
              required
              className="w-full bg-surface-container-low border border-transparent focus:border-outline-variant focus:ring-0 rounded-md px-4 py-3 font-mono text-sm transition-all"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-on-surface-variant opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer text-sm">
              link
            </span>
          </div>
          <p className="text-xs text-on-surface-variant mt-1">
            The base URL for your EDR provider API
          </p>
        </div>
        
        {isSentinelOne && (
          <div className="space-y-2">
            <label className="block text-sm font-semibold uppercase tracking-widest text-on-surface-variant">
              XDR Base URL
            </label>
            <div className="relative group">
              <input
                type="text"
                name="config.xdr_base_url"
                value={formData.config.xdr_base_url}
                onChange={handleInputChange}
                placeholder="Optional: https://xdr.example.sentinelone.net"
                className="w-full bg-surface-container-low border border-transparent focus:border-outline-variant focus:ring-0 rounded-md px-4 py-3 font-mono text-sm transition-all"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-on-surface-variant opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer text-sm">
                link
              </span>
            </div>
            <p className="text-xs text-on-surface-variant mt-1">
              Optional. Use this only if SentinelOne provides a separate SDL/XDR endpoint for your tenant.
            </p>
          </div>
        )}
      </>
    );
  };

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center ${show ? 'block' : 'hidden'}`}>
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onHide}
      />
      
      {/* Modal Content */}
      <div className="relative bg-surface-container-lowest rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.12)] max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-outline-variant/20">
          <h2 className="text-xl font-semibold">{title}</h2>
          <button
            onClick={onHide}
            className="text-on-surface-variant hover:text-on-surface transition-colors"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Provider Type */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold uppercase tracking-widest text-on-surface-variant">
              Provider Type
            </label>
            <Dropdown autoClose="outside">
              <Dropdown.Toggle
                className="report-dropdown-toggle w-100 text-start"
                disabled={!!initialData}
              >
                <span className="text-truncate">
                  {formData.provider === 'sentinelone' ? 'SentinelOne' :
                   formData.provider === 'crowdstrike' ? 'CrowdStrike' :
                   formData.provider === 'microsoft_defender' ? 'Microsoft Defender' :
                   formData.provider === 'tanium' ? 'Tanium' : 'Select provider type'}
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="report-dropdown-menu w-100">
                <button
                  type="button"
                  className={`report-dropdown-item ${formData.provider === 'sentinelone' ? 'selected' : ''}`}
                  onClick={() => handleInputChange({ target: { name: 'provider', value: 'sentinelone' } })}
                  disabled={!!initialData}
                >
                  <span className="report-dropdown-icon">
                    {formData.provider === 'sentinelone' ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                  </span>
                  <span>SentinelOne</span>
                </button>
                <button
                  type="button"
                  className={`report-dropdown-item ${formData.provider === 'crowdstrike' ? 'selected' : ''}`}
                  onClick={() => handleInputChange({ target: { name: 'provider', value: 'crowdstrike' } })}
                  disabled={!!initialData}
                >
                  <span className="report-dropdown-icon">
                    {formData.provider === 'crowdstrike' ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                  </span>
                  <span>CrowdStrike</span>
                </button>
                <button
                  type="button"
                  className={`report-dropdown-item ${formData.provider === 'microsoft_defender' ? 'selected' : ''}`}
                  onClick={() => handleInputChange({ target: { name: 'provider', value: 'microsoft_defender' } })}
                  disabled={!!initialData}
                >
                  <span className="report-dropdown-icon">
                    {formData.provider === 'microsoft_defender' ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                  </span>
                  <span>Microsoft Defender</span>
                </button>
                <button
                  type="button"
                  className={`report-dropdown-item ${formData.provider === 'tanium' ? 'selected' : ''}`}
                  onClick={() => handleInputChange({ target: { name: 'provider', value: 'tanium' } })}
                  disabled={!!initialData}
                >
                  <span className="report-dropdown-icon">
                    {formData.provider === 'tanium' ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                  </span>
                  <span>Tanium</span>
                </button>
              </Dropdown.Menu>
            </Dropdown>
          </div>

          {/* Name */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold uppercase tracking-widest text-on-surface-variant">
              Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter a descriptive name"
              required
              className="w-full bg-surface-container-low border border-transparent focus:border-outline-variant focus:ring-0 rounded-md px-4 py-3 text-sm transition-all"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold uppercase tracking-widest text-on-surface-variant">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Describe this EDR provider configuration"
              rows={3}
              className="w-full bg-surface-container-low border border-transparent focus:border-outline-variant focus:ring-0 rounded-md px-4 py-3 text-sm transition-all resize-none"
            />
          </div>

          {/* Configuration Section */}
          <div className="space-y-6">
            <div className="pt-6 border-t border-outline-variant/20">
              <h3 className="text-lg font-semibold mb-4">Provider Configuration</h3>
              {renderConfigFields()}
            </div>
          </div>

          {/* Enable Checkbox */}
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              name="enabled"
              checked={formData.enabled}
              onChange={handleInputChange}
              className="w-4 h-4 rounded border-outline-variant text-primary focus:ring-primary"
            />
            <label className="text-sm font-medium">Enable this provider</label>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-outline-variant/20">
            <button
              type="button"
              onClick={onHide}
              className="px-6 py-2.5 rounded-md text-sm font-semibold border border-outline-variant hover:bg-surface-container transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-2.5 rounded-md text-sm font-semibold bg-primary text-on-primary hover:bg-primary-container transition-all active:scale-95 flex items-center space-x-2"
            >
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-on-primary"></div>
              )}
              <span>{initialData ? 'Update' : 'Create'} Provider</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EDRManagement;
